import React, { useEffect, useState, useReducer } from 'react';
import { Formik } from 'formik';
import * as yup from 'yup';
import { useNavigate, useOutletContext, useLocation } from 'react-router-dom';
import { ColDiv, Container, RowDiv, Span, Text, SubWrap } from 'styles';
import { Input, SubTitle, Textarea, Checkbox, Button, InputFormik, FileInput2 } from 'components';
import { Alert } from 'components/modal/Alert';
import { getByteSize } from 'utils';
import { BOARD_QNA_MODULE_NO } from 'data/constants';
import boardService from 'services/board/board-service';
import * as S from './QnaStyle';

function reducerValues(state, action) {
  if (action) {
    return {
      regUserName: action.regUserName || '',
      regUserPhone: action.regUserPhone || '',
      regUserEmail: action.regUserEmail || '',
      regPassword: '',
      passwordCheck: '',
      title: action.title || '',
      content: action.content || '',
      useSecret: action.useSecret || false,
      uploadFile: action.attachFiles ? { name: action.attachFiles[0].originalFileName } : null
    };
  }
  return state;
}

export function QnaWrite(props) {
  const location = useLocation();
  const navigate = useNavigate();

  const { contentNo, password } = location.state || {};

  const { langList } = useOutletContext();
  const [isNew, setIsNew] = useState(true);
  const [agree, setAgree] = useState(false);
  const [privateQna, setPrivateQna] = useState(false);

  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState(false);
  const [initialValues, dispatchValues] = useReducer(reducerValues, {
    regUserName: '',
    regUserPhone: '',
    regUserEmail: '',
    regPassword: '',
    passwordCheck: '',
    title: '',
    content: '',
    useSecret: false,
    uploadFile: null
  });

  const fetchQna = async (password) => {
    let data = {};

    try {
      const response = await boardService.getContent(BOARD_QNA_MODULE_NO, contentNo, password);

      if (response) {
        data = response;
      }
    } catch (e) {
      console.error(e);
    }

    if (data.canAccess) {
      dispatchValues(data.content);
      if (data.content.useSecret) {
        setPrivateQna(true);
      }
    }
  };

  const agreeCheck = (val, name, checked) => {
    if (checked) {
      setAgree(true);
    } else {
      setAgree(false);
    }
  };

  const privateQnaCheck = (val, name, checked) => {
    if (checked) {
      setPrivateQna(true);
    } else {
      setPrivateQna(false);
    }
  };

  const handleOnSubmit = async (values) => {
    if (!agree) {
      setAlert({
        content: langList.qnaForm.errorMessages.requiredPolicy,
        confirm: {
          onClick: () => {
            setAlert(null);
          }
        }
      });
      return;
    }

    if (!saving) {
      setSaving(true);
      const form = document.getElementById('form-qna');
      const data = new FormData(form);

      if (!privateQna) {
        data.delete('regPassword');
        data.delete('passwordCheck');
      }

      let res;
      if (isNew) {
        res = await boardService.saveContent(data);
      } else {
        res = await boardService.updateContent(data);
      }

      setSaving(false);

      if (res) {
        setAlert({
          content: langList.qnaForm.errorMessages.saveDone,
          confirm: {
            onClick: () => {
              setAlert(null);
              navigate('/community/qna');
            }
          }
        });
      }
    }
  };

  const schema = yup.object().shape({
    regUserName: yup
      .string()
      .required(langList.qnaForm.errorMessages.required)
      .test('maxByte', '최대 입력값은 100 바이트(byte) 입니다.', (val) => getByteSize(val) <= 100),
    regUserPhone: yup
      .string()
      .required(langList.qnaForm.errorMessages.required)
      .matches(/^\d+$/, langList.qnaForm.errorMessages.invalidPhone)
      .test('maxByte', '최대 입력값은 20 바이트(byte) 입니다.', (val) => getByteSize(val) <= 20),
    regUserEmail: yup
      .string()
      .required(langList.qnaForm.errorMessages.required)
      .email(langList.qnaForm.errorMessages.invalidEmail)
      .test('maxByte', '최대 입력값은 100 바이트(byte) 입니다.', (val) => getByteSize(val) <= 100),
    title: yup
      .string()
      .required(langList.qnaForm.errorMessages.required)
      .test('maxByte', '최대 입력값은 255 바이트(byte) 입니다.', (val) => getByteSize(val) <= 255),
    regPassword: yup.string().concat(
      privateQna && isNew
        ? yup
            .string()
            .required(langList.qnaForm.errorMessages.required)
            .test('minByte', '최소 입력값은 4 바이트(byte) 입니다.', (val) => getByteSize(val) >= 4)
        : null
    ),
    passwordCheck: yup.string().concat(
      privateQna && isNew
        ? yup
            .string()
            .required(langList.qnaForm.errorMessages.required)
            .test('minByte', '최소 입력값은 4 바이트(byte) 입니다.', (val) => getByteSize(val) >= 4)
            .oneOf([yup.ref('regPassword'), null], '비밀번호가 일치하지 않습니다')
        : null
    )
  });

  useEffect(() => {
    if (contentNo && password) {
      fetchQna(password);
      setIsNew(false);
    }
  }, [contentNo, password]);

  return (
    <>
      {alert && <Alert {...alert} />}
      <Formik initialValues={initialValues} validationSchema={schema} onSubmit={handleOnSubmit} enableReinitialize>
        {(formik) => (
          <ColDiv $width="100%" $gap={24}>
            <SubWrap $type="first">
              <Container $gap={48}>
                <SubTitle title={langList.qnaForm.subject} desc={langList.qnaForm.subjectDesc} />
                <S.ContentBox>
                  <form id="form-qna" onSubmit={formik.handleSubmit}>
                    <input type="hidden" name="moduleNo" value={BOARD_QNA_MODULE_NO} />
                    <input type="hidden" name="contentNo" value={contentNo} />
                    <input type="hidden" name="useSecret" value={formik.values.useSecret} />
                    <S.GridDiv>
                      <ColDiv $gap={8} $gridColumn={2}>
                        <Text $type="b6" $mType="b7" $fontWeight={500}>
                          {langList.qnaForm.question.filter((q) => q.name === 'userName')[0].label}&nbsp;<Span $color="alert">*</Span>
                        </Text>
                        <InputFormik
                          type="text"
                          placeholder={langList.qnaForm.question.filter((q) => q.name === 'userName')[0].placeholder}
                          {...formik.getFieldProps('regUserName')}
                        />
                        {formik.touched.regUserName && formik.errors.regUserName ? <div className="error">{formik.errors.regUserName}</div> : null}
                      </ColDiv>

                      <ColDiv $gap={8} $gridColumn={2}>
                        <Text $type="b6" $mType="b7" $fontWeight={500}>
                          {langList.qnaForm.question.filter((q) => q.name === 'tel')[0].label} <Span $color="alert">*</Span>
                        </Text>
                        <InputFormik
                          type="text"
                          placeholder={langList.qnaForm.question.filter((q) => q.name === 'tel')[0].placeholder}
                          {...formik.getFieldProps('regUserPhone')}
                        />
                        {formik.touched.regUserPhone && formik.errors.regUserPhone ? <div className="error">{formik.errors.regUserPhone}</div> : null}
                      </ColDiv>

                      <ColDiv $gap={8} $gridColumn={2}>
                        <Text $type="b6" $mType="b7" $fontWeight={500}>
                          {langList.qnaForm.question.filter((q) => q.name === 'email')[0].label} <Span $color="alert">*</Span>
                        </Text>
                        <InputFormik
                          type="text"
                          placeholder={langList.qnaForm.question.filter((q) => q.name === 'email')[0].placeholder}
                          {...formik.getFieldProps('regUserEmail')}
                        />
                        {formik.touched.regUserEmail && formik.errors.regUserEmail ? <div className="error">{formik.errors.regUserEmail}</div> : null}
                      </ColDiv>

                      <ColDiv $gap={8} $gridColumn={6}>
                        <Checkbox checked={privateQna} onChange={privateQnaCheck}>
                          비밀글로 문의하기
                        </Checkbox>
                      </ColDiv>

                      {privateQna && (
                        <>
                          <ColDiv $gap={8} $gridColumn={3}>
                            <Text $type="b6" $mType="b7" $fontWeight={500}>
                              {langList.qnaForm.question.filter((q) => q.name === 'password')[0].label}
                            </Text>
                            <InputFormik
                              type="password"
                              placeholder={langList.qnaForm.question.filter((q) => q.name === 'password')[0].placeholder}
                              {...formik.getFieldProps('regPassword')}
                            />
                            {formik.touched.regPassword && formik.errors.regPassword ? <div className="error">{formik.errors.regPassword}</div> : null}
                          </ColDiv>

                          <ColDiv $gap={8} $gridColumn={3}>
                            <Text $type="b6" $mType="b7" $fontWeight={500}>
                              {langList.qnaForm.question.filter((q) => q.name === 'passwordCheck')[0].label}
                            </Text>
                            <InputFormik
                              type="password"
                              placeholder={langList.qnaForm.question.filter((q) => q.name === 'passwordCheck')[0].placeholder}
                              {...formik.getFieldProps('passwordCheck')}
                            />
                            {formik.touched.passwordCheck && formik.errors.passwordCheck ? (
                              <div className="error">{formik.errors.passwordCheck}</div>
                            ) : null}
                          </ColDiv>
                        </>
                      )}

                      <ColDiv $gap={8} $gridColumn={6}>
                        <Text $type="b6" $mType="b7" $fontWeight={500}>
                          {langList.qnaForm.question.filter((q) => q.name === 'subject')[0].label} <Span $color="alert">*</Span>
                        </Text>
                        <InputFormik
                          type="text"
                          placeholder={langList.qnaForm.question.filter((q) => q.name === 'subject')[0].placeholder}
                          {...formik.getFieldProps('title')}
                        />
                        {formik.touched.title && formik.errors.title ? <div className="error">{formik.errors.title}</div> : null}
                      </ColDiv>

                      <ColDiv $gap={8} $gridColumn={6}>
                        <Text $type="b6" $mType="b7" $fontWeight={500}>
                          {langList.qnaForm.question.filter((q) => q.name === 'content')[0].label}
                        </Text>

                        <Textarea
                          placeholder={langList.qnaForm.question.filter((q) => q.name === 'content')[0].placeholder}
                          name="content"
                          className={`${formik.errors.content ? 'is-invalid' : ''}`}
                          value={formik.values.content}
                          onChangeFormik={formik.handleChange}
                        />

                        {formik.errors.content ? <div className="error">{formik.errors.content}</div> : null}
                      </ColDiv>
                    </S.GridDiv>
                    <ColDiv $gap={8} $padding="24px 0" $mPadding="16px 0 0">
                      <Text $type="b6" $mType="b7" $fontWeight={500}>
                        {langList.qnaForm.uploadFile}
                      </Text>
                      <FileInput2
                        id="uploadFile"
                        name="attachments"
                        label={langList.qnaForm.uploadInputLabel}
                        desc={formik.values.uploadFile ? undefined : langList.qnaForm.uploadDesc}
                        onChange={(e) => {
                          formik.setFieldValue('uploadFile', e.currentTarget.files[0]);
                        }}
                      />
                      {formik.values.uploadFile && (
                        <RowDiv $gap={16}>
                          <Input
                            name="testFile"
                            value={formik.values?.uploadFile?.name}
                            del
                            disabled
                            delClick={(e) => {
                              const uploadFile = document.getElementById('uploadFile');
                              uploadFile.value = '';
                              formik.setFieldValue('uploadFile', null);
                            }}
                          />
                        </RowDiv>
                      )}
                    </ColDiv>
                  </form>
                </S.ContentBox>
              </Container>
              <RowDiv $gap={8} $padding="0 24px">
                <Checkbox checked={agree} onChange={agreeCheck}>
                  {langList.qnaForm.agree}
                </Checkbox>
                <Text $type="b7" $fontWeight={500} $color="secondary300">
                  {langList.qnaForm.agreeView}
                </Text>
              </RowDiv>
              <S.ButtonBox>
                <Button label={langList.qnaForm.cancelBtn} btnType="xLarge" btnStyle="cancel" onClick={() => navigate('/community/qna')} />
                <Button
                  label={langList.qnaForm.submitBtn}
                  btnType="xLarge"
                  btnStyle="fill"
                  onClick={(e) => {
                    formik.submitForm();
                  }}
                />
              </S.ButtonBox>
            </SubWrap>
          </ColDiv>
        )}
      </Formik>
    </>
  );
}
